import * as React from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Plus, Pencil, Trash2, X } from 'lucide-react';
import { z } from 'zod';
import { toast } from 'sonner';

import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { ConfirmDialog } from '@/components/data/ConfirmDialog';
import { EmptyState } from '@/components/data/EmptyState';
import { AuditChip } from '@/components/audit/AuditChip';
import {
  DragSortableList,
  DragHandle,
} from '@/components/data/DragSortableList';
import { SectionForm } from '@/features/homepage/SectionForm';
import { AddItemForm } from '@/features/homepage/AddItemForm';
import { HomepagePreviewPanel } from '@/features/homepage/HomepagePreview';
import {
  useDeleteHomepageItem,
  useDeleteHomepageSection,
  useHomepagePreview,
  useHomepageSections,
  usePatchHomepageSection,
  useReorderHomepageItem,
} from '@/queries/homepage';
import { useApiError } from '@/lib/useApiError';
import { useQueryClient } from '@tanstack/react-query';
import { qk } from '@/queries/queryKeys';
import type {
  HomepagePreview,
  HomepageSection,
} from '@/schemas/admin/homepage';
import { cn } from '@/lib/cn';

const searchSchema = z.object({
  section: z.string().optional(),
  view: z.enum(['edit', 'preview']).optional(),
});

export const Route = createFileRoute('/_authed/curation/homepage')({
  validateSearch: searchSchema,
  component: HomepageCurationScreen,
});

function HomepageCurationScreen() {
  const { t } = useTranslation('homepage');
  const search = Route.useSearch();
  const navigate = useNavigate();
  const sections = useHomepageSections();
  const resolveError = useApiError();

  const [creatingSection, setCreatingSection] = React.useState(false);
  const [editingSection, setEditingSection] = React.useState<HomepageSection | null>(null);
  const [deletingSection, setDeletingSection] = React.useState<HomepageSection | null>(null);
  const [addingItemFor, setAddingItemFor] = React.useState<string | null>(null);

  const deleteSection = useDeleteHomepageSection();

  const selectedSection = React.useMemo(
    () => sections.data?.find((s) => s.id === search.section) ?? null,
    [sections.data, search.section]
  );

  function pickSection(id: string | null) {
    void navigate({
      to: '/curation/homepage',
      search: (prev) => ({ ...prev, section: id ?? undefined }),
      replace: true,
    });
  }

  function setView(view: 'edit' | 'preview') {
    void navigate({
      to: '/curation/homepage',
      search: (prev) => ({ ...prev, view }),
      replace: true,
    });
  }

  async function handleDeleteSection(s: HomepageSection) {
    try {
      await deleteSection.mutateAsync(s.id);
      toast.success(t('sections.deleted'));
      setDeletingSection(null);
      if (search.section === s.id) pickSection(null);
    } catch (err) {
      const resolved = resolveError(err);
      toast.error(resolved.message, resolved.recovery ? { description: resolved.recovery } : undefined);
    }
  }

  const view = search.view ?? 'edit';

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">{t('title')}</h1>
          <p className="max-w-2xl text-sm text-[color:var(--color-fg-muted)]">{t('subtitle')}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <AuditChip />
          <div
            role="tablist"
            aria-label={t('preview.title')}
            className="inline-flex rounded-[var(--radius-md)] border border-[color:var(--color-border)] bg-[color:var(--color-surface-2)] p-0.5"
          >
            <button
              role="tab"
              aria-selected={view === 'edit'}
              type="button"
              onClick={() => setView('edit')}
              className={cn(
                'rounded-[var(--radius-sm)] px-3 py-1.5 text-xs font-medium',
                view === 'edit'
                  ? 'bg-[color:var(--color-surface)] text-[color:var(--color-fg)] shadow-sm'
                  : 'text-[color:var(--color-fg-muted)]'
              )}
            >
              {t('sections.title')}
            </button>
            <button
              role="tab"
              aria-selected={view === 'preview'}
              type="button"
              onClick={() => setView('preview')}
              className={cn(
                'rounded-[var(--radius-sm)] px-3 py-1.5 text-xs font-medium',
                view === 'preview'
                  ? 'bg-[color:var(--color-surface)] text-[color:var(--color-fg)] shadow-sm'
                  : 'text-[color:var(--color-fg-muted)]'
              )}
            >
              {t('preview.title')}
            </button>
          </div>
        </div>
      </header>

      {view === 'preview' ? (
        <Card>
          <CardContent className="pt-6">
            <HomepagePreviewPanel />
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[18rem_1fr]">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between gap-2">
              <CardTitle className="text-base">{t('sections.title')}</CardTitle>
              <Button size="sm" onClick={() => setCreatingSection(true)}>
                <Plus className="size-4" aria-hidden />
                {t('sections.newSection')}
              </Button>
            </CardHeader>
            <CardContent>
              <SectionList
                data={sections.data ?? []}
                loading={sections.isLoading}
                selectedId={selectedSection?.id ?? null}
                onSelect={(id) => pickSection(id)}
                onEdit={(s) => setEditingSection(s)}
                onDelete={(s) => setDeletingSection(s)}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between gap-2">
              <CardTitle className="text-base">{t('items.title')}</CardTitle>
              {selectedSection ? (
                <Button size="sm" onClick={() => setAddingItemFor(selectedSection.id)}>
                  <Plus className="size-4" aria-hidden />
                  {t('items.newItem')}
                </Button>
              ) : null}
            </CardHeader>
            <CardContent>
              {selectedSection ? (
                <ItemList
                  sectionId={selectedSection.id}
                  sectionTitle={selectedSection.title_en ?? selectedSection.slug}
                />
              ) : (
                <EmptyState title={t('items.title')} body={t('items.selectSection')} />
              )}
            </CardContent>
          </Card>
        </div>
      )}

      <Dialog open={creatingSection} onOpenChange={setCreatingSection}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t('sections.newSection')}</DialogTitle>
            <DialogDescription>{t('subtitle')}</DialogDescription>
          </DialogHeader>
          <SectionForm
            onSaved={() => setCreatingSection(false)}
            onCancel={() => setCreatingSection(false)}
            defaultPosition={(sections.data?.length ?? 0)}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(editingSection)} onOpenChange={(o) => !o && setEditingSection(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t('sections.editSection')}</DialogTitle>
            <DialogDescription>
              {t('sections.editSectionBody', {
                defaultValue:
                  'Update titles, kind, position, and active state. Items stay attached to this section.',
              })}
            </DialogDescription>
          </DialogHeader>
          {editingSection ? (
            <SectionForm
              section={editingSection}
              onSaved={() => setEditingSection(null)}
              onCancel={() => setEditingSection(null)}
            />
          ) : null}
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(addingItemFor)} onOpenChange={(o) => !o && setAddingItemFor(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('items.addTitle')}</DialogTitle>
            <DialogDescription>{t('items.addBody')}</DialogDescription>
          </DialogHeader>
          {addingItemFor ? (
            <AddItemForm
              sectionId={addingItemFor}
              onSaved={() => setAddingItemFor(null)}
              onCancel={() => setAddingItemFor(null)}
            />
          ) : null}
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={Boolean(deletingSection)}
        onOpenChange={(o) => !o && setDeletingSection(null)}
        title={t('sections.deleteConfirmTitle')}
        description={t('sections.deleteConfirmBody')}
        destructive
        loading={deleteSection.isPending}
        onConfirm={() => {
          if (deletingSection) void handleDeleteSection(deletingSection);
        }}
      />
    </div>
  );
}

function SectionList({
  data,
  loading,
  selectedId,
  onSelect,
  onEdit,
  onDelete,
}: {
  data: readonly HomepageSection[];
  loading: boolean;
  selectedId: string | null;
  onSelect: (id: string) => void;
  onEdit: (s: HomepageSection) => void;
  onDelete: (s: HomepageSection) => void;
}) {
  const { t } = useTranslation('homepage');
  const queryClient = useQueryClient();
  const patch = usePatchHomepageSection();
  const resolveError = useApiError();

  if (loading) {
    return (
      <ul className="space-y-2" aria-busy="true">
        {Array.from({ length: 4 }).map((_, i) => (
          <li
            key={i}
            className="h-12 animate-pulse rounded-[var(--radius-md)] bg-[color:var(--color-surface-3)]"
          />
        ))}
      </ul>
    );
  }
  if (data.length === 0) {
    return <EmptyState title={t('sections.empty')} body={t('sections.emptyBody')} />;
  }
  const ordered = [...data].sort((a, b) => a.position - b.position);

  async function handleReorder(
    next: HomepageSection[],
    change: { id: string; from: number; to: number }
  ) {
    if (change.from === change.to) return;
    const moved = next.find((s) => s.id === change.id);
    if (!moved) return;
    // Optimistically reflect the new section order so the sidebar updates
    // without waiting for the round-trip.
    const previous = queryClient.getQueryData<HomepageSection[]>(qk.homepageSections());
    queryClient.setQueryData<HomepageSection[]>(
      qk.homepageSections(),
      next.map((s, i) => ({ ...s, position: i }))
    );
    try {
      await patch.mutateAsync({
        id: moved.id,
        input: {
          slug: moved.slug,
          kind: moved.kind,
          position: change.to,
          active: moved.active,
        },
      });
      toast.success(t('sections.reordered', { defaultValue: 'Section order updated.' }));
    } catch (err) {
      if (previous) queryClient.setQueryData(qk.homepageSections(), previous);
      const resolved = resolveError(err);
      toast.error(resolved.message, resolved.recovery ? { description: resolved.recovery } : undefined);
    }
  }

  return (
    <DragSortableList
      items={ordered}
      onReorder={(next, change) => void handleReorder(next, change)}
      disabled={patch.isPending}
      renderItem={(s, handle) => {
        const selected = s.id === selectedId;
        return (
          <div
            className={cn(
              'flex items-center justify-between gap-2 rounded-[var(--radius-md)] border bg-[color:var(--color-surface)] px-2 py-2',
              selected
                ? 'border-[color:var(--color-accent)] ring-1 ring-[color:var(--color-accent)]'
                : 'border-[color:var(--color-border)]'
            )}
          >
            <DragHandle handle={handle} />
            <button
              type="button"
              onClick={() => onSelect(s.id)}
              className="flex min-w-0 flex-1 flex-col items-start gap-0.5 text-start"
            >
              <span className="flex items-center gap-2 text-sm font-medium">
                <span>{s.title_en ?? s.slug}</span>
                {!s.active ? (
                  <Badge tone="neutral">
                    {t('sections.active', { defaultValue: 'inactive' })}
                  </Badge>
                ) : null}
              </span>
              <span dir="ltr" className="text-xs text-[color:var(--color-fg-muted)]">
                {`${s.kind} · #${s.position}`}
              </span>
            </button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => onEdit(s)}
              aria-label={t('sections.editSection')}
            >
              <Pencil className="size-4" aria-hidden />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => onDelete(s)}
              aria-label={t('sections.deleteSection')}
            >
              <Trash2 className="size-4" aria-hidden />
            </Button>
          </div>
        );
      }}
    />
  );
}

interface PreviewItem {
  id: string;
  subjectKind: string;
  subjectId: string;
  position: number;
  title: string | null;
}

function ItemList({ sectionId, sectionTitle }: { sectionId: string; sectionTitle: string }) {
  const { t } = useTranslation('homepage');
  const queryClient = useQueryClient();
  const reorder = useReorderHomepageItem();
  const removeItem = useDeleteHomepageItem();
  const resolveError = useApiError();
  // Pull from the EN preview to populate this section's items — the preview
  // endpoint is the only documented way to enumerate composed items.
  const en = useHomepagePreview('en');
  const [pendingRemoval, setPendingRemoval] = React.useState<string | null>(null);

  const items = React.useMemo<PreviewItem[]>(() => {
    if (!en.data) return [];
    const sections = (en.data.sections ?? []) as readonly NonNullable<HomepagePreview['sections']>[number][];
    const match = sections.find((s) => s.id === sectionId);
    if (!match?.items) return [];
    return match.items
      .filter((it): it is NonNullable<typeof it> & { id: string; subject_kind: string; subject_id: string; position: number } =>
        Boolean(it.id) && Boolean(it.subject_kind) && Boolean(it.subject_id) && typeof it.position === 'number'
      )
      .map((it) => ({
        id: it.id,
        subjectKind: it.subject_kind,
        subjectId: it.subject_id,
        position: it.position,
        title: it.title ?? null,
      }))
      .sort((a, b) => a.position - b.position);
  }, [en.data, sectionId]);

  if (en.isLoading) {
    return (
      <div className="space-y-2" aria-busy="true">
        {Array.from({ length: 3 }).map((_, i) => (
          <div
            key={i}
            className="h-14 animate-pulse rounded-[var(--radius-md)] bg-[color:var(--color-surface-3)]"
          />
        ))}
      </div>
    );
  }
  if (items.length === 0) {
    return <EmptyState title={t('items.empty')} body={t('items.emptyBody')} />;
  }

  async function handleReorder(
    next: PreviewItem[],
    change: { id: string; from: number; to: number }
  ) {
    // Optimistically reflect the new order in the EN preview cache (rendering
    // the panel updates instantly), then call the API. On failure refetch to
    // realign with the server (sentinel-swap is transactional).
    const previous = queryClient.getQueryData<HomepagePreview>(qk.homepagePreview('en'));
    if (previous) {
      const sections = (previous.sections ?? []) as readonly NonNullable<HomepagePreview['sections']>[number][];
      const updated = {
        ...previous,
        sections: sections.map((s) => {
          if (s.id !== sectionId) return s;
          return {
            ...s,
            items: next.map((it, i) => ({
              id: it.id,
              subject_kind: it.subjectKind,
              subject_id: it.subjectId,
              position: i,
              title: it.title,
            })),
          };
        }),
      };
      queryClient.setQueryData(qk.homepagePreview('en'), updated);
    }
    try {
      await reorder.mutateAsync({ id: change.id, input: { position: change.to } });
      toast.success(t('items.reorderToast'));
    } catch (err) {
      const resolved = resolveError(err);
      toast.error(t('items.reorderRollback'), {
        description: resolved.recovery ?? resolved.message,
      });
      void queryClient.invalidateQueries({ queryKey: ['homepage'] });
    }
  }

  async function handleRemove(id: string) {
    try {
      await removeItem.mutateAsync(id);
      toast.success(t('items.removed'));
    } catch (err) {
      const resolved = resolveError(err);
      toast.error(resolved.message, resolved.recovery ? { description: resolved.recovery } : undefined);
    } finally {
      setPendingRemoval(null);
    }
  }

  return (
    <>
      <p className="mb-2 text-xs text-[color:var(--color-fg-muted)]">
        {sectionTitle}
      </p>
      <DragSortableList
        items={items}
        onReorder={(next, change) => void handleReorder(next, change)}
        renderItem={(item, handle) => (
          <div className="flex items-center gap-2 px-2 py-2">
            <DragHandle handle={handle} />
            <div className="flex min-w-0 flex-1 flex-col">
              <span className="truncate text-sm">{item.title ?? item.subjectId}</span>
              <span dir="ltr" className="font-mono text-xs text-[color:var(--color-fg-muted)]">
                {item.subjectKind} · {item.subjectId.slice(0, 8)}…
              </span>
            </div>
            <Button
              size="sm"
              variant="ghost"
              aria-label={t('items.remove')}
              onClick={() => setPendingRemoval(item.id)}
            >
              <X className="size-4" aria-hidden />
            </Button>
          </div>
        )}
      />
      <ConfirmDialog
        open={Boolean(pendingRemoval)}
        onOpenChange={(o) => !o && setPendingRemoval(null)}
        title={t('items.removeConfirm')}
        destructive
        loading={removeItem.isPending}
        onConfirm={() => {
          if (pendingRemoval) void handleRemove(pendingRemoval);
        }}
      />
    </>
  );
}
